import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import SEO from './SEO';
import { brandedProductImageUrl } from '../utils/imageUrls';
import { productPath } from '../utils/productUrls';
import './ProductSearch.css';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const PAGE_SIZE = 24;

const PRODUCT_TYPES = [
  'body care',
  'conditioner',
  'device / tool',
  'exfoliant / peel',
  'eye product',
  'face mask',
  'face wash / cleanser',
  'foundation / complexion makeup',
  'fragrance',
  'gift / set',
  'hair treatment',
  'lip product',
  'makeup',
  'moisturizer / cream',
  'oil',
  'other skincare',
  'serum',
  'shampoo',
  'spot / acne treatment',
  'supplement',
  'sunscreen / spf',
  'toner / essence',
];

function escapeSearchValue(value) {
  return value.replace(/[,*()]/g, ' ').replace(/\s+/g, ' ').trim();
}

function searchTerms(value) {
  return escapeSearchValue(value)
    .split(' ')
    .map((term) => term.trim())
    .filter((term) => term.length >= 2)
    .slice(0, 6);
}

function productTextFilter(term) {
  const like = `*${term}*`;
  return `or(search_text.ilike.${like},name.ilike.${like},brand.ilike.${like})`;
}

function buildProductsUrl({ query, productType, safetyFilter, page }) {
  const params = new URLSearchParams();
  params.set(
    'select',
    [
      'id',
      'retailer',
      'brand',
      'name',
      'category',
      'product_type',
      'url',
      'is_acne_safe',
      'acne_safety_status',
      'unsafe_ingredients',
      'popularity_score',
      'stored_image_url',
      'image_url',
    ].join(',')
  );
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(page * PAGE_SIZE));
  params.set('retailer', 'in.(dermstore,sephora,ulta,target,walgreens,face_reality)');

  if (productType) {
    params.set('product_type', `eq.${productType}`);
  }

  if (safetyFilter === 'safe') {
    params.set('is_acne_safe', 'eq.true');
  } else if (safetyFilter === 'not_safe') {
    params.set('acne_safety_status', 'eq.not_acne_safe');
  } else if (safetyFilter === 'unknown') {
    params.set('or', '(acne_safety_status.eq.unknown_ingredients,acne_safety_status.is.null)');
  }

  const terms = searchTerms(query);
  params.set('order', terms.length > 0 ? 'name.asc' : 'popularity_score.desc,name.asc');
  if (terms.length > 0) {
    const textFilters = terms.map(productTextFilter);
    const combinedFilters =
      safetyFilter === 'unknown'
        ? ['or(acne_safety_status.eq.unknown_ingredients,acne_safety_status.is.null)', ...textFilters]
        : textFilters;

    if (safetyFilter === 'unknown') {
      params.delete('or');
    }
    params.set('and', `(${combinedFilters.join(',')})`);
  }

  return `${SUPABASE_URL}/rest/v1/products?${params.toString()}`;
}

function statusLabel(product) {
  const unsafeIngredients = Array.isArray(product.unsafe_ingredients) ? product.unsafe_ingredients : [];
  if (product.is_acne_safe === true) return 'Acne-Safe';
  if (product.acne_safety_status === 'not_acne_safe') {
    return unsafeIngredients.length ? `${unsafeIngredients.length} flagged` : 'Not acne safe';
  }
  return 'No ingredients';
}

function statusClass(product) {
  if (product.is_acne_safe === true) return 'is-safe';
  if (product.acne_safety_status === 'not_acne_safe') return 'is-not-safe';
  return 'is-unknown';
}

function statusIcon(product) {
  if (product.is_acne_safe === true) return '✓';
  if (product.acne_safety_status === 'not_acne_safe') return '!';
  return '?';
}

function retailerLabel(retailer) {
  const labels = {
    dermstore: 'Dermstore',
    sephora: 'Sephora',
    ulta: 'Ulta',
    target: 'Target',
    walgreens: 'Walgreens',
    face_reality: 'Face Reality',
  };
  return labels[(retailer || '').toLowerCase()] || retailer || '';
}

function ProductCard({ product }) {
  const originalImageUrl = product.stored_image_url || product.image_url;
  const imageUrl = brandedProductImageUrl(originalImageUrl);
  const detailPath = productPath(product);

  return (
    <article className="product-result">
      <Link className="product-result-link" to={detailPath} aria-label={`View ${product.brand || ''} ${product.name || 'product'}`.trim()}>
        <div className="product-image-frame">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              onError={(event) => {
                if (originalImageUrl && event.currentTarget.src !== originalImageUrl) {
                  event.currentTarget.src = originalImageUrl;
                }
              }}
            />
          ) : (
            <div className="product-image-placeholder" aria-hidden="true">□</div>
          )}
        </div>
        <div className="product-result-body">
          {product.brand && <p className="product-brand">{product.brand}</p>}
          <h2>{product.name}</h2>
          <div className="product-result-meta">
            <span className={`product-status ${statusClass(product)}`}>
              <span className="product-status-icon" aria-hidden="true">{statusIcon(product)}</span>
              {statusLabel(product)}
            </span>
            {product.retailer && <span className="product-retailer">{retailerLabel(product.retailer)}</span>}
          </div>
        </div>
        <span className="product-chevron" aria-hidden="true">›</span>
      </Link>
    </article>
  );
}

function ProductSkeleton() {
  return (
    <div className="product-result product-skeleton" aria-hidden="true">
      <div className="skeleton-image" />
      <div className="skeleton-copy">
        <span className="skeleton-line short" />
        <span className="skeleton-line" />
        <span className="skeleton-line medium" />
      </div>
    </div>
  );
}

function ProductSearch() {
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [productType, setProductType] = useState('');
  const [safetyFilter, setSafetyFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const loadMoreRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  const isConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

  const activeFilters = useMemo(
    () => [submittedQuery, productType, safetyFilter].filter(Boolean).join('|'),
    [submittedQuery, productType, safetyFilter]
  );

  useEffect(() => {
    if (!isConfigured) return;

    let isCurrent = true;
    async function fetchProducts() {
      setIsLoading(true);
      setError('');

      try {
        const response = await fetch(
          buildProductsUrl({
            query: submittedQuery,
            productType,
            safetyFilter,
            page,
          }),
          {
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            },
          }
        );

        if (response.status === 416) {
          if (!isCurrent) return;
          setHasMore(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`Product search failed (${response.status})`);
        }

        const rows = await response.json();
        if (!isCurrent) return;

        setProducts((current) => (page === 0 ? rows : [...current, ...rows]));
        setHasMore(rows.length === PAGE_SIZE);
      } catch (err) {
        if (!isCurrent) return;
        setError(err.message || 'Product search failed.');
      } finally {
        if (isCurrent) {
          isLoadingMoreRef.current = false;
          setIsLoading(false);
        }
      }
    }

    fetchProducts();
    return () => {
      isCurrent = false;
    };
  }, [activeFilters, page, isConfigured, productType, safetyFilter, submittedQuery]);

  useEffect(() => {
    if (!isConfigured || !hasMore || isLoading || error) return undefined;

    const loadMoreNode = loadMoreRef.current;
    if (!loadMoreNode || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMoreRef.current) {
          isLoadingMoreRef.current = true;
          setPage((current) => current + 1);
        }
      },
      { rootMargin: '600px 0px' }
    );

    observer.observe(loadMoreNode);
    return () => observer.disconnect();
  }, [error, hasMore, isConfigured, isLoading]);

  function runSearch(event) {
    event.preventDefault();
    isLoadingMoreRef.current = false;
    setPage(0);
    setProducts([]);
    setSubmittedQuery(query.trim());
  }

  function clearSearch() {
    isLoadingMoreRef.current = false;
    setQuery('');
    setSubmittedQuery('');
    setPage(0);
    setProducts([]);
  }

  function changeProductType(event) {
    isLoadingMoreRef.current = false;
    setPage(0);
    setProducts([]);
    setProductType(event.target.value);
  }

  function changeSafetyFilter(value) {
    isLoadingMoreRef.current = false;
    setPage(0);
    setProducts([]);
    setSafetyFilter(value);
  }

  return (
    <div className="App">
      <SEO
        title="Acne Safe Product Search | Acne Safe"
        description="Search Dermstore, Sephora, Ulta, Target, and Walgreens skincare, makeup, hair, body, and sun-care products by acne safety status, product type, brand, and ingredient flags."
      />
      <Header />
      <main className="product-search-page">
        <section className="product-search-header">
          <h1>Product Search</h1>
          <p>Search by brand or product name to check its ingredients.</p>
        </section>

        <section className="product-search-controls" aria-label="Product filters">
          <form className="product-search-form" onSubmit={runSearch}>
            <div className="product-search-row">
              <svg className="product-search-icon" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m16.5 16.5 4 4" />
              </svg>
              <input
                id="product-query"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by brand or product name..."
              />
              {query && (
                <button className="product-search-clear" type="button" onClick={clearSearch} aria-label="Clear search">
                  ×
                </button>
              )}
              <button type="submit">Search</button>
            </div>
          </form>

          <div className="product-filter-grid">
            <label>
              <select value={productType} onChange={changeProductType}>
                <option value="">All product types</option>
                {PRODUCT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>

            <div className="safety-filter" role="group" aria-label="Acne safety">
              <button
                type="button"
                className={safetyFilter === 'all' ? 'active' : ''}
                onClick={() => changeSafetyFilter('all')}
              >
                All
              </button>
              <button
                type="button"
                className={safetyFilter === 'safe' ? 'active' : ''}
                onClick={() => changeSafetyFilter('safe')}
              >
                Safe
              </button>
              <button
                type="button"
                className={safetyFilter === 'not_safe' ? 'active' : ''}
                onClick={() => changeSafetyFilter('not_safe')}
              >
                Not safe
              </button>
              <button
                type="button"
                className={safetyFilter === 'unknown' ? 'active' : ''}
                onClick={() => changeSafetyFilter('unknown')}
              >
                Unknown
              </button>
            </div>
          </div>
        </section>

        {!isConfigured && (
          <section className="product-search-message">
            Add `REACT_APP_SUPABASE_URL` and `REACT_APP_SUPABASE_ANON_KEY` to the site environment to enable product search.
          </section>
        )}

        {error && <section className="product-search-message error">{error}</section>}

        {isConfigured && !error && products.length > 0 && (
          <section className="product-results-summary" aria-live="polite">
            <h2>{submittedQuery ? `Results for “${submittedQuery}”` : 'Popular products'}</h2>
            <p>{submittedQuery ? `${products.length} products shown` : 'Products people check most often'}</p>
          </section>
        )}

        <section className="product-results-grid">
          {isConfigured && !error && isLoading && products.length === 0 && (
            Array.from({ length: 6 }, (_, index) => <ProductSkeleton key={index} />)
          )}
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </section>

        {isConfigured && !isLoading && products.length === 0 && !error && (
          <section className="product-search-message product-search-empty">
            <span aria-hidden="true">⌕</span>
            <h2>No products found</h2>
            <p>Try the brand name or a shorter product title.</p>
          </section>
        )}

        {hasMore && (
          <div className="load-more-row" ref={loadMoreRef}>
            <button
              type="button"
              onClick={() => {
                if (isLoadingMoreRef.current) return;
                isLoadingMoreRef.current = true;
                setPage((current) => current + 1);
              }}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Load more products'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default ProductSearch;
